import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  ShoppingBag, 
  Edit, 
  Check, 
  Package, 
  Truck, 
  Calendar,
  User,
  FileText,
  AlertCircle,
  Plus,
  Minus
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { achatService } from '../../services/achatService';
import { supplierService } from '../../services/supplierService';
import { productService } from '../../services/productService';
import { AchatOrder } from '../../types';

function AchatDetail() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const { addNotification } = useNotifications();
  
  const [order, setOrder] = useState<AchatOrder | null>(null);
  const [loading, setLoading] = useState(true);
  const [showReceiveModal, setShowReceiveModal] = useState(false);
  const [receivingQuantities, setReceivingQuantities] = useState<Record<string, number>>({});

  useEffect(() => {
    if (id) {
      loadOrderData();
    }
  }, [id]);

  const loadOrderData = async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      const orderData = await achatService.getAchatOrder(id);
      setOrder(orderData);
      
      // Initialiser les quantités de réception
      if (orderData) {
        const quantities: Record<string, number> = {};
        orderData.items.forEach(item => {
          quantities[item.productId] = item.quantity - item.receivedQuantity;
        });
        setReceivingQuantities(quantities);
      }
    } catch (error) {
      addNotification({
        userId: user?.id || '',
        title: 'Erreur de chargement',
        message: 'Impossible de charger les détails de la commande',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleValidateOrder = async () => {
    if (!order || !user) return;
    
    try {
      await achatService.validateOrder(order.id, user.id);
      addNotification({
        userId: user.id,
        title: 'Commande validée',
        message: 'La commande a été validée et envoyée au fournisseur',
        type: 'success'
      });
      loadOrderData();
    } catch (error) {
      addNotification({
        userId: user?.id || '',
        title: 'Erreur',
        message: 'Impossible de valider la commande',
        type: 'error'
      });
    }
  };

  const handleReceiveItems = async () => {
    if (!order || !user) return;
    
    try {
      await achatService.receiveOrderItems(order.id, receivingQuantities, user.id);
      addNotification({
        userId: user.id,
        title: 'Réception enregistrée',
        message: 'Les articles ont été réceptionnés et le stock mis à jour',
        type: 'success'
      });
      setShowReceiveModal(false);
      loadOrderData();
    } catch (error) {
      addNotification({
        userId: user?.id || '',
        title: 'Erreur',
        message: 'Impossible d\'enregistrer la réception',
        type: 'error'
      });
    }
  };

  const canManageOrder = user && order && (
    user.role === 'admin' || 
    user.role === 'responsable_depot' || 
    user.role === 'responsable_bureau'
  );

  const canReceiveOrder = user && order && (
    canManageOrder || user.role === 'responsable_equipe'
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="text-center py-12">
        <ShoppingBag className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">Commande non trouvée</h3>
        <Link to="/achats" className="mt-2 text-blue-600 hover:text-blue-800">
          Retour à la liste
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="glass-card rounded-xl card-hover">
        <div className="px-6 py-6 sm:p-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="p-2 bg-gradient-to-br from-green-500 to-green-700 rounded-lg shadow-lg">
                <ShoppingBag className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Commande #{order.orderNumber}</h1>
                <p className="text-gray-600">{supplierService.getSupplierName(order.supplierId)}</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              {canManageOrder && order.status === 'en_attente_validation' && (
                <button
                  onClick={handleValidateOrder}
                  className="btn-primary px-4 py-2 text-white text-sm rounded-lg font-medium transition-all duration-200 flex items-center space-x-2"
                >
                  <Check className="h-4 w-4" />
                  <span>Valider</span>
                </button>
              )}
              
              {canReceiveOrder && (order.status === 'en_attente_livraison' || order.status === 'livree_partiellement') && (
                <button
                  onClick={() => setShowReceiveModal(true)}
                  className="btn-secondary px-4 py-2 text-white text-sm rounded-lg font-medium transition-all duration-200 flex items-center space-x-2"
                >
                  <Package className="h-4 w-4" />
                  <span>Réceptionner</span>
                </button>
              )}
              
              {canManageOrder && order.status === 'en_attente_validation' && (
                <Link
                  to={`/achats/${order.id}/edit`}
                  className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                  title="Modifier"
                >
                  <Edit className="h-5 w-5" />
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Order Info */}
          <div className="glass-card rounded-xl">
            <div className="px-6 py-5 sm:px-8">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Informations commande</h3>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Statut
                  </label>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    achatService.getStatusColor(order.status)
                  }`}>
                    {achatService.getStatusLabel(order.status)}
                  </span>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Créé par
                  </label>
                  <div className="flex items-center">
                    <User className="h-4 w-4 text-gray-400 mr-2" />
                    <span className="text-sm text-gray-900">{order.createdBy}</span>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date de création
                  </label>
                  <div className="flex items-center">
                    <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                    <span className="text-sm text-gray-900">
                      {new Date(order.createdAt).toLocaleDateString('fr-FR')}
                    </span>
                  </div>
                </div>
                
                {order.expectedDelivery && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Livraison prévue
                    </label>
                    <div className="flex items-center">
                      <Truck className="h-4 w-4 text-gray-400 mr-2" />
                      <span className="text-sm text-gray-900">
                        {new Date(order.expectedDelivery).toLocaleDateString('fr-FR')}
                      </span>
                    </div>
                  </div>
                )}
              </div>
              
              {order.notes && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes
                  </label>
                  <p className="text-sm text-gray-900">{order.notes}</p>
                </div>
              )}
            </div>
          </div>

          {/* Order Items */}
          <div className="glass-card rounded-xl">
            <div className="px-6 py-5 sm:px-8 border-b border-gray-200/50">
              <h3 className="text-lg font-medium text-gray-900">
                Articles commandés ({order.items.length})
              </h3>
            </div>
            
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50/50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Produit
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Qté commandée
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Qté reçue
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Prix unitaire
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Total
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white/50 divide-y divide-gray-200">
                  {order.items.map((item) => (
                    <tr key={item.productId} className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {productService.getProductName ? productService.getProductName(item.productId) : item.productId}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {item.quantity}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <span className="text-sm text-gray-900 mr-2">{item.receivedQuantity}</span>
                          {item.receivedQuantity < item.quantity && (
                            <AlertCircle className="h-4 w-4 text-orange-500" />
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {item.unitPrice.toFixed(2)} €
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {item.totalPrice.toFixed(2)} €
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Order Summary */}
          <div className="glass-card rounded-xl">
            <div className="px-6 py-5 border-b border-gray-200/50">
              <h3 className="text-lg font-medium text-gray-900">Résumé</h3>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Articles</span>
                <span className="text-sm font-medium text-gray-900">{order.items.length}</span>
              </div>
              
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Quantité totale</span>
                <span className="text-sm font-medium text-gray-900">
                  {order.items.reduce((sum, item) => sum + item.quantity, 0)}
                </span>
              </div>
              
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Quantité reçue</span>
                <span className="text-sm font-medium text-gray-900">
                  {order.items.reduce((sum, item) => sum + item.receivedQuantity, 0)}
                </span>
              </div>
              
              <div className="border-t pt-4">
                <div className="flex justify-between">
                  <span className="text-base font-medium text-gray-900">Total</span>
                  <span className="text-base font-bold text-gray-900">
                    {order.totalAmount.toFixed(2)} € HT
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="glass-card rounded-xl">
            <div className="px-6 py-5 border-b border-gray-200/50">
              <h3 className="text-lg font-medium text-gray-900">Actions rapides</h3>
            </div>
            
            <div className="p-6 space-y-3">
              {canManageOrder && order.status === 'en_attente_validation' && (
                <button
                  onClick={handleValidateOrder}
                  className="w-full btn-primary px-4 py-2 text-white text-sm rounded-lg font-medium transition-all duration-200 flex items-center justify-center space-x-2"
                >
                  <Check className="h-4 w-4" />
                  <span>Valider la commande</span>
                </button>
              )}
              
              {canReceiveOrder && (order.status === 'en_attente_livraison' || order.status === 'livree_partiellement') && (
                <button
                  onClick={() => setShowReceiveModal(true)}
                  className="w-full btn-secondary px-4 py-2 text-white text-sm rounded-lg font-medium transition-all duration-200 flex items-center justify-center space-x-2"
                >
                  <Package className="h-4 w-4" />
                  <span>Réceptionner</span>
                </button>
              )}
              
              <button className="w-full bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 text-sm rounded-lg font-medium transition-colors flex items-center justify-center space-x-2">
                <FileText className="h-4 w-4" />
                <span>Imprimer bon de commande</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Receive Modal */}
      {showReceiveModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Réceptionner la commande
              </h3>
              
              <div className="space-y-4 max-h-96 overflow-y-auto">
                {order.items.map((item) => {
                  const remaining = item.quantity - item.receivedQuantity;
                  if (remaining <= 0) return null;
                  
                  return (
                    <div key={item.productId} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-900">
                          {productService.getProductName ? productService.getProductName(item.productId) : item.productId}
                        </div>
                        <div className="text-xs text-gray-500">
                          Commandé: {item.quantity} | Reçu: {item.receivedQuantity} | Restant: {remaining}
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => setReceivingQuantities(prev => ({
                            ...prev,
                            [item.productId]: Math.max(0, (prev[item.productId] || 0) - 1)
                          }))}
                          className="p-1 text-gray-400 hover:text-gray-600"
                        >
                          <Minus className="h-4 w-4" />
                        </button>
                        
                        <input
                          type="number"
                          min="0"
                          max={remaining}
                          value={receivingQuantities[item.productId] || 0}
                          onChange={(e) => setReceivingQuantities(prev => ({
                            ...prev,
                            [item.productId]: Math.min(remaining, Math.max(0, parseInt(e.target.value) || 0))
                          }))}
                          className="w-16 px-2 py-1 text-center border border-gray-300 rounded text-sm"
                        />
                        
                        <button
                          onClick={() => setReceivingQuantities(prev => ({
                            ...prev,
                            [item.productId]: Math.min(remaining, (prev[item.productId] || 0) + 1)
                          }))}
                          className="p-1 text-gray-400 hover:text-gray-600"
                        >
                          <Plus className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
              
              <div className="flex items-center justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowReceiveModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={handleReceiveItems}
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-md transition-colors"
                >
                  Réceptionner
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AchatDetail;